/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * SSF-346 / AC #3 — Shared Extend plan-family → i18n key resolver.
 *
 * The PDP banner and the cart-row toggle both render the protection-plan
 * label ("Accident Protection" / "Furniture Protection" /
 * "Mattress Protection"). Source of truth is the cartridge-supplied
 * `planFamily` discriminator on each Offer object — see
 * `cartridges/int_sfnext_extend/cartridge/rest-apis/ashley-extend/script.js`
 * `deriveFamily()` for the server-side rules.
 *
 * Surfaces that consume this util keep zero business logic about
 * "what's a mattress" — they just pass the offer's `planFamily` /
 * `planType` and, when available, the product's
 * `c_mattressProtectorToggle` attribute. The util applies the same
 * priority the cartridge does so the storefront stays consistent both
 * before and after the cartridge ships an explicit `planFamily`.
 *
 * Priority (must stay in lockstep with `deriveFamily()` in the cartridge):
 *   1. `offer.planFamily` (when present) — authoritative.
 *   2. `c_mattressProtectorToggle === true` → mattress (mattress wins
 *      regardless of plan type, per AC #3).
 *   3. `planType === 'adh'` → accident.
 *   4. Otherwise → furniture.
 */

export type ExtendPlanFamily = 'accident' | 'furniture' | 'mattress';

export interface ExtendPlanFamilyInputs {
    /** Cartridge-supplied family. When present this short-circuits the fallback chain. */
    planFamily?: ExtendPlanFamily | null;
    /** Underlying plan type. Used only when `planFamily` is absent. */
    planType?: 'adh' | 'base' | null;
    /**
     * Product's mattress signal — same attribute the cartridge reads in
     * `deriveFamily()`. Pass when the consumer has product context (PDP
     * inside `<ProductViewProvider>`). Cart row consumers usually don't
     * carry this; the cartridge-supplied `planFamily` covers them.
     */
    mattressProtectorToggle?: boolean | null;
}

/**
 * Map plan-family inputs to a concrete `ExtendPlanFamily`. Defaults to
 * `'furniture'` only when every signal is absent — preserving the
 * pre-SSF-346 behavior for the silent fallthrough case.
 */
export function resolveExtendPlanFamily({
    planFamily,
    planType,
    mattressProtectorToggle,
}: ExtendPlanFamilyInputs): ExtendPlanFamily {
    if (planFamily) return planFamily;
    if (mattressProtectorToggle === true) return 'mattress';
    if (planType === 'adh') return 'accident';
    return 'furniture';
}

/** i18n key on the `extendProtectionPlan` namespace that renders the
 *  plan-name label for the resolved family. */
export function getPlanFamilyLabelKey(
    family: ExtendPlanFamily
): 'accidentProtection' | 'furnitureProtection' | 'mattressProtection' {
    if (family === 'accident') return 'accidentProtection';
    if (family === 'mattress') return 'mattressProtection';
    return 'furnitureProtection';
}

/** i18n key on the `extendProtectionPlan` namespace for the fallback
 *  subheading copy when the upstream Extend response carries no
 *  `marketingText`. */
export function getPlanFamilyDefaultDescriptionKey(
    family: ExtendPlanFamily
): 'defaultDescriptionAccident' | 'defaultDescriptionFurniture' | 'defaultDescriptionMattress' {
    if (family === 'accident') return 'defaultDescriptionAccident';
    if (family === 'mattress') return 'defaultDescriptionMattress';
    return 'defaultDescriptionFurniture';
}
